import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, CalendarPlus, DollarSign, Save, Loader2, Scissors, Package, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseDataSemFuso, normalizarTelefone } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useLanificios } from "@/hooks/useLanificios";
import { useTiposItemAvulso } from "@/hooks/useTiposItemAvulso";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import HistoricoCliente from "@/components/atividades/HistoricoCliente";
import NovaAtividadeDialog from "@/components/atividades/NovaAtividadeDialog";

type ProvaInfo = { id: string; created_at: string };
type ItemResumo = { tipo_item: string; quantidade: number };
type PedidoResumo = { valor_total: number; pago: boolean; itens: ItemResumo[] };

export default function ClienteDetalhes() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cliente, setCliente] = useState<any>(null);
  const [fichas, setFichas] = useState<any[]>([]);
  const [provasByFichaId, setProvasByFichaId] = useState<Map<string, ProvaInfo[]>>(new Map());
  const [pedidosByFichaId, setPedidosByFichaId] = useState<Map<string, PedidoResumo[]>>(new Map());
  const [vendedorNomes, setVendedorNomes] = useState<Map<string, string>>(new Map());
  const [formData, setFormData] = useState({ nome: "", telefone: "" });
  const [novaAtividadeAberta, setNovaAtividadeAberta] = useState(false);
  const { data: lanificios = [] } = useLanificios();
  const { data: tiposItem = [] } = useTiposItemAvulso();

  // Nome do lanifício por id (lookup); fallback no texto legado paleto_lanificio.
  const nomeLanificio = (ficha: any): string | null => {
    if (ficha.lanificio_id) {
      const encontrado = lanificios.find(l => l.id === ficha.lanificio_id);
      if (encontrado) return encontrado.nome;
    }
    return ficha.paleto_lanificio || null;
  };

  useEffect(() => {
    const loadClienteData = async () => {
      if (!id) { navigate("/clientes"); return; }
      setLoading(true);
      try {
        const { data: clienteData, error: clienteError } = await supabase
          .from('clientes').select('*').eq('id', id).single();
        if (clienteError) throw clienteError;
        if (!clienteData) { navigate("/clientes"); return; }

        setCliente(clienteData);
        setFormData({ nome: clienteData.nome || "", telefone: clienteData.telefone || "" });

        const { data: fichasData, error: fichasError } = await supabase
          .from('fichas').select('*').eq('cliente_id', id).order('created_at', { ascending: false });
        if (fichasError) throw fichasError;
        const loadedFichas = fichasData || [];
        setFichas(loadedFichas);

        if (loadedFichas.length > 0) {
          const fichaIds = loadedFichas.map((f: any) => f.id);
          const vendedorIds = [...new Set(
            loadedFichas.map((f: any) => f.vendedor_id).filter(Boolean)
          )] as string[];

          const [provasRes, pedidosRes, profilesRes] = await Promise.all([
            supabase.from('provas').select('id, ficha_id, created_at').in('ficha_id', fichaIds).order('created_at'),
            supabase.from('pedidos').select('id, ficha_id, valor_total, pago, itens_avulsos_ficha(tipo_item, quantidade)').in('ficha_id', fichaIds),
            vendedorIds.length > 0
              ? supabase.from('profiles').select('id, nome').in('id', vendedorIds)
              : Promise.resolve({ data: [] as any[], error: null }),
          ]);

          const provasMap = new Map<string, ProvaInfo[]>();
          for (const p of (provasRes.data ?? [])) {
            const arr = provasMap.get(p.ficha_id) ?? [];
            arr.push({ id: p.id, created_at: p.created_at });
            provasMap.set(p.ficha_id, arr);
          }
          setProvasByFichaId(provasMap);

          const pedidosMap = new Map<string, PedidoResumo[]>();
          for (const p of (pedidosRes.data ?? [])) {
            const arr = pedidosMap.get(p.ficha_id) ?? [];
            arr.push({
              valor_total: p.valor_total,
              pago: p.pago,
              itens: ((p as any).itens_avulsos_ficha ?? []).filter((i: ItemResumo) => i.quantidade > 0),
            });
            pedidosMap.set(p.ficha_id, arr);
          }
          setPedidosByFichaId(pedidosMap);

          const nomesMap = new Map<string, string>();
          for (const p of (profilesRes.data ?? [])) {
            nomesMap.set(p.id, p.nome || "");
          }
          setVendedorNomes(nomesMap);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do cliente:", error);
      } finally {
        setLoading(false);
      }
    };
    loadClienteData();
  }, [id, navigate]);

  const handleSave = async () => {
    if (!id) return;

    // Normaliza o telefone (mesma regra do banco) antes de gravar. Telefone é a
    // chave de unicidade do cliente; se não normalizar, avisa e não salva.
    let telefoneNormalizado: string | null = null;
    if (formData.telefone.trim() !== '') {
      telefoneNormalizado = normalizarTelefone(formData.telefone);
      if (!telefoneNormalizado) {
        toast({
          title: "Telefone inválido",
          description: "Não foi possível normalizar o telefone. Confira o formato.",
          variant: "destructive",
        });
        return;
      }
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('clientes')
        .update({ nome: formData.nome, telefone: telefoneNormalizado, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      const { data: clienteData } = await supabase.from('clientes').select('*').eq('id', id).single();
      if (clienteData) setCliente(clienteData);
      toast({ title: "Cadastro atualizado" });
    } catch (error) {
      console.error("Erro ao atualizar cliente:", error);
      const msg = error instanceof Error ? error.message : "";
      const duplicado = msg.includes('uq_clientes_telefone') || msg.includes('duplicate') || msg.includes('23505');
      toast({
        title: duplicado ? "Telefone já cadastrado" : "Erro ao atualizar cadastro",
        description: duplicado
          ? "Já existe outro cliente com este telefone."
          : (msg || "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getTipoColor = (tipo?: string) => {
    if (!tipo) return "bg-muted text-muted-foreground";
    switch (tipo.toLowerCase()) {
      case "aluguel": return "bg-blue-500/10 text-blue-700 dark:text-blue-400";
      case "venda": return "bg-green-500/10 text-green-700 dark:text-green-400";
      case "ajuste": return "bg-purple-500/10 text-purple-700 dark:text-purple-400";
      case "avulso": return "bg-amber-500/10 text-amber-700 dark:text-amber-400";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const labelItemAvulso = (slug: string) =>
    tiposItem.find(t => t.slug === slug)?.nome ?? (slug.charAt(0).toUpperCase() + slug.slice(1));

  const formatValor = (v?: any) => {
    if (v === null || v === undefined || v === "") return null;
    const n = parseFloat(String(v).replace(",", "."));
    if (Number.isNaN(n)) return null;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
  };

  const joinParts = (...parts: (string | null | undefined)[]) =>
    parts.filter(Boolean).join(" · ");

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header title="Carregando..." />
        <main className="flex-1 p-4 pb-20 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Detalhes do Cliente" />

      <main className="flex-1 p-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/clientes')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold">Detalhes do Cliente</h1>
          </div>

          {/* Informações do Cliente */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <Avatar className="w-20 h-20">
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                    {formData.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold mb-1">{cliente?.nome}</h2>
                  <p className="text-sm text-muted-foreground">
                    Cliente desde {cliente?.created_at ? format(new Date(cliente.created_at), "dd/MM/yyyy", { locale: ptBR }) : "N/A"}
                  </p>
                </div>
              </div>

              <Separator className="mb-6" />

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input id="nome" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} placeholder="Nome completo" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input id="telefone" value={formData.telefone} onChange={(e) => setFormData({ ...formData, telefone: e.target.value })} placeholder="(00) 00000-0000" />
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  Salvar Alterações
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Atividades do cliente (timeline + criação rápida) */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Atividades</h3>
              <Button size="sm" variant="outline" onClick={() => setNovaAtividadeAberta(true)}>
                <CalendarPlus className="h-4 w-4 mr-1" />
                Nova atividade
              </Button>
            </div>
            <Card>
              <CardContent className="p-4 max-h-80 overflow-y-auto">
                {id && <HistoricoCliente clienteId={id} />}
              </CardContent>
            </Card>
          </div>

          {/* Histórico de Fichas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Histórico de Fichas</h3>

            {fichas.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground">Nenhuma ficha encontrada para este cliente.</p>
                </CardContent>
              </Card>
            ) : (
              fichas.map((ficha) => {
                const provas = provasByFichaId.get(ficha.id) ?? [];
                const pedidosFicha = pedidosByFichaId.get(ficha.id) ?? [];
                const totalAvulsos = pedidosFicha.reduce((acc, p) => acc + Number(p.valor_total ?? 0), 0);
                const vendedorNome = ficha.vendedor_id ? vendedorNomes.get(ficha.vendedor_id) : undefined;
                const isNaoAtiva = ficha.status === 'pendente' || ficha.status === 'erro';

                const paletoParts = joinParts(
                  ficha.paleto ? `Paletó ${ficha.paleto}` : null,
                  ficha.paleto_cor,
                  ficha.tipo === 'venda' ? nomeLanificio(ficha) : null,
                  ficha.sob_medida ? 'Sob medida' : null,
                  ficha.tipo === 'aluguel' ? ficha.paleto_categoria : null,
                );
                const calcaParts = ficha.calca ? `Calça ${ficha.calca}` : null;
                const camisaParts = joinParts(ficha.camisa ? `Camisa ${ficha.camisa}` : null, ficha.camisa_fios ? `${ficha.camisa_fios} fios` : null, ficha.camisa_cor);
                const sapatoParts = joinParts(ficha.sapato ? `Sapato ${ficha.sapato}` : null, ficha.sapato_tipo);

                const hasItemDetails = !!(paletoParts || calcaParts || camisaParts || sapatoParts);
                const hasValores = !!(ficha.valor || ficha.garantia || totalAvulsos > 0);
                const hasDatas = !!(ficha.data_retirada || ficha.data_devolucao || ficha.data_festa);

                const isAvulso = ficha.status === 'avulso';

                if (isAvulso) {
                  const pedido = pedidosFicha[0];
                  const itensAvulso = pedido?.itens ?? [];
                  const itensTexto = itensAvulso
                    .map((i) => `${labelItemAvulso(i.tipo_item)} × ${i.quantidade}`)
                    .join(' · ');

                  return (
                    <Card key={ficha.id} className="overflow-hidden border-l-4 border-l-amber-500">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm">
                                #{ficha.codigo_ficha || "Sem código"}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Criado em {format(new Date(ficha.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                              </p>
                              {vendedorNome && (
                                <div className="flex items-center gap-1 mt-1">
                                  <User className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">{vendedorNome}</span>
                                </div>
                              )}
                            </div>
                            <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400">
                              Pedido Avulso
                            </Badge>
                          </div>

                          {/* Itens do pedido */}
                          {itensTexto && (
                            <>
                              <Separator />
                              <div className="flex items-start gap-1.5">
                                <Package className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <p className="text-xs">{itensTexto}</p>
                              </div>
                            </>
                          )}

                          {/* Valores */}
                          {(totalAvulsos > 0 || pedido?.valor_total > 0) && (
                            <>
                              <Separator />
                              <div className="space-y-1.5">
                                {(pedido?.valor_total ?? 0) > 0 && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <DollarSign className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-muted-foreground">Valor total:</span>
                                    <span className="font-medium">{formatValor(pedido.valor_total)}</span>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* Pagamento */}
                          <div className="flex items-center justify-between pt-1">
                            <span className="text-xs text-muted-foreground">Pagamento:</span>
                            <Badge variant={pedido?.pago ? "default" : "secondary"}>
                              {pedido?.pago ? "Pago" : "Pendente"}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }

                return (
                  <Card
                    key={ficha.id}
                    className="overflow-hidden cursor-pointer hover:shadow-md transition-all"
                    onClick={() => navigate(`/editar-ficha-v3/${ficha.id}`, { state: { cliente_id: id } })}
                  >
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">
                              #{ficha.codigo_ficha || "Sem código"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Criado em {format(new Date(ficha.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                            </p>
                            {vendedorNome && (
                              <div className="flex items-center gap-1 mt-1">
                                <User className="w-3 h-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">{vendedorNome}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1.5 items-end">
                            <Badge className={getTipoColor(ficha.tipo)}>
                              {ficha.tipo || "N/A"}
                            </Badge>
                            {isNaoAtiva && (
                              <Badge className={ficha.status === 'pendente' ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" : "bg-red-500/10 text-red-700 dark:text-red-400"}>
                                {ficha.status === 'pendente' ? 'Pendente' : 'Erro'}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Datas */}
                        {hasDatas && (
                          <>
                            <Separator />
                            <div className="space-y-1.5">
                              {ficha.data_retirada && (
                                <div className="flex items-center gap-2 text-xs">
                                  <Calendar className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Retirada:</span>
                                  <span className="font-medium">{format(parseDataSemFuso(ficha.data_retirada)!, "dd/MM/yyyy", { locale: ptBR })}</span>
                                </div>
                              )}
                              {ficha.data_devolucao && (
                                <div className="flex items-center gap-2 text-xs">
                                  <Calendar className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Devolução:</span>
                                  <span className="font-medium">{format(parseDataSemFuso(ficha.data_devolucao)!, "dd/MM/yyyy", { locale: ptBR })}</span>
                                </div>
                              )}
                              {ficha.data_festa && (
                                <div className="flex items-center gap-2 text-xs">
                                  <Calendar className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Festa:</span>
                                  <span className="font-medium">{format(parseDataSemFuso(ficha.data_festa)!, "dd/MM/yyyy", { locale: ptBR })}</span>
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {/* Peças com detalhes */}
                        {hasItemDetails && (
                          <>
                            <Separator />
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Peças:</p>
                              <div className="space-y-0.5 text-xs">
                                {paletoParts && <p className="truncate">{paletoParts}</p>}
                                {calcaParts && <p className="truncate">{calcaParts}</p>}
                                {camisaParts && <p className="truncate">{camisaParts}</p>}
                                {sapatoParts && <p className="truncate">{sapatoParts}</p>}
                              </div>
                            </div>
                          </>
                        )}

                        {/* Provas */}
                        {provas.length > 0 && (
                          <>
                            <Separator />
                            <div className="flex items-start gap-1.5">
                              <Scissors className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                              <p className="text-xs">
                                <span className="text-muted-foreground">Provas ({provas.length}): </span>
                                {provas.map(p => format(new Date(p.created_at), "dd/MM", { locale: ptBR })).join(" · ")}
                              </p>
                            </div>
                          </>
                        )}

                        {/* Pedidos avulsos */}
                        {pedidosFicha.length > 0 && (
                          <>
                            <Separator />
                            <div className="flex items-start gap-1.5">
                              <Package className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                              <p className="text-xs">
                                <span className="text-muted-foreground">Pedidos avulsos: </span>
                                {pedidosFicha.length} pedido{pedidosFicha.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </>
                        )}

                        {/* Valores e pagamento */}
                        {hasValores && (
                          <>
                            <Separator />
                            <div className="space-y-1.5">
                              {ficha.valor && (
                                <div className="flex items-center gap-2 text-xs">
                                  <DollarSign className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Valor:</span>
                                  <span className="font-medium">{formatValor(ficha.valor)}</span>
                                </div>
                              )}
                              {ficha.garantia && (
                                <div className="flex items-center gap-2 text-xs">
                                  <DollarSign className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Garantia:</span>
                                  <span className="font-medium">{formatValor(ficha.garantia)}</span>
                                </div>
                              )}
                              {totalAvulsos > 0 && (
                                <div className="flex items-center gap-2 text-xs">
                                  <DollarSign className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Valor avulsos:</span>
                                  <span className="font-medium">{formatValor(totalAvulsos)}</span>
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {/* Pagamento */}
                        <div className="flex items-center justify-between pt-1">
                          <span className="text-xs text-muted-foreground">Pagamento:</span>
                          <Badge variant={ficha.pago ? "default" : "secondary"}>
                            {ficha.pago ? "Pago" : "Pendente"}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </main>

      {cliente && (
        <NovaAtividadeDialog
          open={novaAtividadeAberta}
          onClose={() => setNovaAtividadeAberta(false)}
          clienteInicial={{ id: cliente.id, nome: cliente.nome }}
        />
      )}
      <BottomNav />
    </div>
  );
}
